import { useRef, useState } from 'react'
import { FiMinus, FiPlus } from 'react-icons/fi'

// Cadran de saisie des heures travaillées : 24 crans de 30 minutes, de 00:30 à 12:00.
const STEP_MINUTES = 30
const STEPS = 24
const DEGREES_PER_STEP = 360 / STEPS
const MAX_MINUTES = STEPS * STEP_MINUTES

// Géométrie, dans un viewBox de 240 centré sur 120.
// Les chiffres et la pastille de l'aiguille partagent le même rayon : le chiffre
// choisi se retrouve dans la pastille et passe en blanc.
const CENTER = 120
const RADIUS_FACE = 106
const RADIUS_TRACK = 100
const RADIUS_TICK = 92
const RADIUS_NUMBER = 70
const KNOB = 15

// "04:30" → 270. Renvoie 0 sur une valeur vide ou mal formée.
export function toMinutes(value) {
    const parts = String(value ?? '').split(':')
    if (parts.length !== 2) return 0
    const hours = parseInt(parts[0], 10)
    const minutes = parseInt(parts[1], 10)
    if (isNaN(hours) || isNaN(minutes)) return 0
    return hours * 60 + minutes
}

// 270 → "04:30", le format attendu par l'API.
export function toDurationValue(minutes) {
    const hours = Math.floor(minutes / 60)
    return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}

// 270 → "4h30", 300 → "5h00"
export function formatDuration(minutes) {
    const hours = Math.floor(minutes / 60)
    return `${hours}h${String(minutes % 60).padStart(2, '0')}`
}

const pointAt = (angle, radius) => {
    const rad = (angle - 90) * Math.PI / 180
    return { x: CENTER + radius * Math.cos(rad), y: CENTER + radius * Math.sin(rad) }
}

const arcPath = (angle, radius) => {
    if (angle <= 0) return ''
    const stop = Math.min(angle, 359.99)
    const start = pointAt(0, radius)
    const end = pointAt(stop, radius)
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${stop > 180 ? 1 : 0} 1 ${end.x} ${end.y}`
}

export default function DurationClock({ value, onChange, totalMinutes = 0, remainingMinutes = 0 }) {
    const svgRef = useRef()
    const dragging = useRef(false)
    const [focused, setFocused] = useState(false)

    const minutes = toMinutes(value)
    const index = minutes ? Math.round(minutes / STEP_MINUTES) : null
    const angle = index ? index * DEGREES_PER_STEP : 0
    const remaining = Math.max(remainingMinutes, 0)
    const over = index !== null && minutes > remaining
    // Heure entière couverte par la pastille, à écrire en blanc
    const coveredHour = index !== null && minutes % 60 === 0 ? minutes / 60 : null

    const setIndex = (next) => {
        const bounded = Math.min(Math.max(next, 1), STEPS)
        onChange(toDurationValue(bounded * STEP_MINUTES))
    }

    // Cran visé par le doigt. On refuse le saut d'un demi-tour pour ne pas
    // passer de 00:30 à 12:00 en glissant à peine au-delà du haut du cadran.
    const indexFromPointer = (event) => {
        const rect = svgRef.current.getBoundingClientRect()
        const dx = event.clientX - (rect.left + rect.width / 2)
        const dy = event.clientY - (rect.top + rect.height / 2)
        let degrees = Math.atan2(dx, -dy) * 180 / Math.PI
        if (degrees < 0) degrees += 360
        let next = Math.round(degrees / DEGREES_PER_STEP)
        if (next === 0) next = STEPS
        if (index !== null && Math.abs(next - index) > STEPS / 2) {
            next = next > index ? 1 : STEPS
        }
        return next
    }

    const onPointerDown = (event) => {
        dragging.current = true
        svgRef.current.setPointerCapture?.(event.pointerId)
        setIndex(indexFromPointer(event))
    }

    const onPointerMove = (event) => {
        if (!dragging.current) return
        setIndex(indexFromPointer(event))
    }

    const onPointerUp = (event) => {
        dragging.current = false
        svgRef.current.releasePointerCapture?.(event.pointerId)
    }

    const onKeyDown = (event) => {
        if (['ArrowUp', 'ArrowRight'].includes(event.key)) {
            event.preventDefault()
            setIndex(index === null ? 1 : index + 1)
        } else if (['ArrowDown', 'ArrowLeft'].includes(event.key)) {
            event.preventDefault()
            setIndex(index === null ? 1 : index - 1)
        }
    }

    const knob = pointAt(angle, RADIUS_NUMBER)

    return (
        <div className={`duration-clock${over ? ' duration-clock--over' : ''}`}>
            <div className="duration-clock__readout">
                <span className="duration-clock__value">
                    {index === null ? '—' : formatDuration(minutes)}
                </span>
                {totalMinutes > 0 && (
                    <span className="duration-clock__hint">
                        {formatDuration(remaining)} restantes sur {formatDuration(totalMinutes)}
                    </span>
                )}
            </div>

            <svg
                ref={svgRef}
                className={`duration-clock__dial${focused ? ' is-focused' : ''}`}
                viewBox="0 0 240 240"
                role="slider"
                tabIndex={0}
                aria-label="Heures travaillées"
                aria-valuemin={STEP_MINUTES}
                aria-valuemax={MAX_MINUTES}
                aria-valuenow={minutes || undefined}
                aria-valuetext={index === null ? 'Aucune durée choisie' : formatDuration(minutes)}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onKeyDown={onKeyDown}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
            >
                <circle className="duration-clock__face" cx={CENTER} cy={CENTER} r={RADIUS_FACE} />

                {remaining > 0 && (
                    <path
                        className="duration-clock__remaining"
                        d={arcPath(Math.min(remaining, MAX_MINUTES) / STEP_MINUTES * DEGREES_PER_STEP, RADIUS_TRACK)}
                    />
                )}

                {Array.from({ length: STEPS }, (unused, position) => {
                    const step = position + 1
                    const tickAngle = step * DEGREES_PER_STEP
                    const isHour = (step * STEP_MINUTES) % 60 === 0
                    const from = pointAt(tickAngle, RADIUS_TICK)
                    const to = pointAt(tickAngle, isHour ? RADIUS_TICK - 6 : RADIUS_TICK - 3)
                    return (
                        <line
                            key={step}
                            className={`duration-clock__tick${isHour ? ' duration-clock__tick--hour' : ''}`}
                            x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                        />
                    )
                })}

                {index !== null && (
                    <g className="duration-clock__hand">
                        <line x1={CENTER} y1={CENTER} x2={knob.x} y2={knob.y} />
                        <circle className="duration-clock__hub" cx={CENTER} cy={CENTER} r={4} />
                        <circle className="duration-clock__knob" cx={knob.x} cy={knob.y} r={KNOB} />
                    </g>
                )}

                {Array.from({ length: 12 }, (unused, position) => {
                    const hour = position + 1
                    const at = pointAt(hour * 30, RADIUS_NUMBER)
                    return (
                        <text
                            key={hour}
                            className={`duration-clock__number${coveredHour === hour ? ' is-selected' : ''}`}
                            x={at.x} y={at.y}
                            textAnchor="middle" dominantBaseline="central"
                        >
                            {hour}
                        </text>
                    )
                })}
            </svg>

            <div className="duration-clock__steps">
                <button type="button" className="btn btn-outline btn-icon"
                    aria-label="Retirer 30 minutes"
                    disabled={index === null || index <= 1}
                    onClick={() => setIndex((index ?? 1) - 1)}>
                    <FiMinus />
                </button>
                <span className="duration-clock__steps-label">30 min</span>
                <button type="button" className="btn btn-outline btn-icon"
                    aria-label="Ajouter 30 minutes"
                    disabled={index !== null && index >= STEPS}
                    onClick={() => setIndex(index === null ? 1 : index + 1)}>
                    <FiPlus />
                </button>
            </div>
        </div>
    )
}
